import { UseMutationOptions, UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query';

import { AxiosResponse } from 'axios';

import { bulkEditCompanies, bulkUpload, createCompany, deleteCompany, editCompany, endpoints, getCompanyDetailsData, getCompanyTableData, mergeCompanyRecords, validateCRMBulkUploadFile } from './api';
import { queryClient } from 'src/app/queryClient';
import { BulkImportRequest, CompanyDataResponse, CompanyDetailResponse, ImportFileResponse } from './apiTypes';
import { invalidateGetContactsQuery } from '../../Contacts/queries';
import { invalidateContactDropdownQuery, invalidateCompanyDropdownQuery } from 'src/features/shared/queries';

//Query keys
const getCompanyQueryKey = (url: string) => ['get-companydata', url];
const getCompanyDetailQuery = (id: number) => [endpoints.getCompanyDetails(id)];

export const invalidateGetCompaniesQuery = () => {
    queryClient.invalidateQueries({ queryKey: ['get-companydata'] });
};

export const invalidateCompanyDetailQuery = (companyId: number) => {
    queryClient.invalidateQueries(getCompanyDetailQuery(companyId));
};

//Queries
export const useGetCompanyQuery = (url: string, options?: Omit<UseQueryOptions<AxiosResponse<CompanyDataResponse>>, 'queryKey' | 'queryFn'>) =>
    useQuery(getCompanyQueryKey(url), () => getCompanyTableData(url), options);

export const useGetCompanyDetails = (id: number, options?: Omit<UseQueryOptions<AxiosResponse>, 'queryKey' | 'queryFn'>) =>
    useQuery(getCompanyDetailQuery(id), () => getCompanyDetailsData(id), { staleTime: 0, ...options } as Omit<UseQueryOptions<AxiosResponse>, 'queryKey' | 'queryFn'>);

//Mutations
export const useCreateCompanyMutation = () =>
    useMutation(createCompany, {
        onSuccess: () => {
            invalidateGetCompaniesQuery();
            invalidateCompanyDropdownQuery();
        }
    });

export const useEditCompanyMutation = () =>
    useMutation(editCompany, {
        onSuccess: (_, request) => {
            const { id } = request;
            invalidateCompanyDetailQuery(id);
            invalidateGetCompaniesQuery();
            invalidateGetContactsQuery();
            invalidateCompanyDropdownQuery();
        }
    });

export const useValidateBulkCRMFileMutation = () => useMutation(validateCRMBulkUploadFile);

export const useBulkUploadMutation = (options?: Omit<UseMutationOptions<AxiosResponse<ImportFileResponse>, unknown, BulkImportRequest, unknown>, 'mutationFn'>) => {
    const { onSuccess, ...rest } = options ?? {};
    return useMutation({
        ...rest, // spread before to prevent overriding of following
        mutationFn: bulkUpload,
        onSuccess: (response, request, context) => {
            invalidateGetCompaniesQuery();
            invalidateGetContactsQuery();
            invalidateCompanyDropdownQuery();
            invalidateContactDropdownQuery();
            if (onSuccess) onSuccess(response, request, context);
        }
    });
};

export const useDeleteCompanyMutation = () =>
    useMutation(deleteCompany, {
        onSuccess: (_, request) => {
            queryClient.setQueriesData<AxiosResponse<CompanyDataResponse>>(['get-companydata'], (oldData) => {
                if (!oldData) return oldData;

                return {
                    ...oldData,
                    data: { ...oldData.data, results: oldData.data.results.filter((item: CompanyDetailResponse) => !request.includes(item.id)) }
                };
            });
            invalidateGetContactsQuery();
            invalidateCompanyDropdownQuery();
            invalidateContactDropdownQuery();
        }
    });

export const useMergeCompanyRecordsMutation = () =>
    useMutation({
        mutationFn: mergeCompanyRecords,
        onSuccess: (_, request) => {
            const { primary_company_id, secondary_company_id } = request;
            queryClient.setQueriesData<AxiosResponse<CompanyDataResponse>>(['get-companydata'], (oldData) => {
                if (!oldData) return oldData;

                return {
                    ...oldData,
                    data: { ...oldData.data, results: oldData.data.results.filter((item) => item.id !== secondary_company_id) }
                };
            });

            // Even though we have removed the secondary_company above, we still need to invalidate list
            // to get the updated fields of primary_company in list after merge, which we cannot override on FE.
            invalidateGetCompaniesQuery();
            invalidateCompanyDetailQuery(primary_company_id);

            invalidateGetContactsQuery();
            invalidateCompanyDropdownQuery();
            invalidateContactDropdownQuery();
        }
    });

export const useBulkEditCompaniesMutation = () =>
    useMutation({
        mutationFn: bulkEditCompanies,
        onSuccess: (_, request) => {
            invalidateGetCompaniesQuery();
            request.company_ids.map((companyId) => invalidateCompanyDetailQuery(companyId));
            invalidateCompanyDropdownQuery();
        }
    });
